import React, { useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Category, ShoppingCategory, ShoppingItem } from '../../../../types';
import CategoryInput, { CategoryInputRef } from './CategoryInput';
import CategoryItem, { CategoryItemRef } from './CategoryItem';
import AddItemForm, { AddItemFormRef } from './AddItemForm';
import { formatCurrency } from '../../../../utils/currencyUtils';

export interface CategoryCardRef {
  resetForm: () => void;
  focusAddItem: () => void;
  focusCategory: () => void;
}

export interface CategoryCardProps {
  categoryId: string;
  category: ShoppingCategory;
  categories: Category[];
  priceMode: boolean;
  total: number;
  onUpdateName: (name: string) => void;
  onRemove: () => void;
  onAddItem: (event: React.FormEvent<HTMLFormElement>) => void;
  onToggleItem: (itemId: string) => void;
  onUpdatePrice: (itemId: string, price: string) => void;
  onUpdateItemName?: (itemId: string, name: string) => void;
  onSavePrice?: (itemId: string) => void;
  onRemoveItem: (itemId: string) => void;
  isAddingItem?: boolean;
  isRemovingItems?: Record<string, boolean>;
  isTogglingItems?: Record<string, boolean>;
}

const CategoryCard = forwardRef<CategoryCardRef, CategoryCardProps>(({
  category,
  categories,
  priceMode,
  total,
  onUpdateName,
  onRemove,
  onAddItem,
  onToggleItem,
  onUpdatePrice,
  onUpdateItemName,
  onSavePrice,
  onRemoveItem,
  isAddingItem = false,
  isRemovingItems = {},
  isTogglingItems = {},
}, ref) => {
  const addItemFormRef = useRef<AddItemFormRef>(null);
  const categoryInputRef = useRef<CategoryInputRef>(null);
  const itemRefs = useRef<Record<string, React.RefObject<CategoryItemRef | null>>>({});
  const previousItemsCountRef = useRef<number>(category.items.length);
  const shouldFocusPriceRef = useRef<boolean>(true);
  const lastShiftKeyRef = useRef<boolean>(false);

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      addItemFormRef.current?.reset();
    },
    focusAddItem: () => {
      addItemFormRef.current?.focus();
    },
    focusCategory: () => {
      categoryInputRef.current?.focus();
    }
  }));

  // Відстежуємо зміну кількості товарів для фокусування на інпуті ціни нового товару
  useEffect(() => {
    const currentItemsCount = category.items.length;
    if (currentItemsCount > previousItemsCountRef.current) {
      if (shouldFocusPriceRef.current) {
        // Новий товар був доданий - знаходимо товар з найновішою датою серед невибраних
        const uncheckedItems = category.items.filter(item => !item.checked);
        if (uncheckedItems.length > 0) {
          const newestItem = uncheckedItems.reduce((newest, current) => {
            const newestDate = newest.createdDate ? new Date(newest.createdDate).getTime() : 0;
            const currentDate = current.createdDate ? new Date(current.createdDate).getTime() : 0;
            return currentDate > newestDate ? current : newest;
          });
          
          requestAnimationFrame(() => {
            const itemRef = itemRefs.current[newestItem.id];
            if (itemRef?.current) {
              itemRef.current.focusPrice();
            }
          });
        }
      } else {
        // При Shift + Enter фокусуємо на інпуті додавання товару
        requestAnimationFrame(() => {
          addItemFormRef.current?.focus();
        });
        shouldFocusPriceRef.current = true; // Скидаємо прапорець для наступного разу
      }
    }
    previousItemsCountRef.current = currentItemsCount;
  }, [category.items.length, category.items]);

  const handleAddItem = (event: React.FormEvent<HTMLFormElement>) => {
    // Перевірити чи був натиснутий SHIFT (з хінта або з інпута)
    // Використовуємо shiftKey з події або збережене значення з handleAddItemKeyDown
    const shiftKey = (event as any).shiftKey !== undefined 
      ? (event as any).shiftKey 
      : lastShiftKeyRef.current;
    
    if (shiftKey) {
      // Shift + Enter: фокус залишається на інпуті додавання товару
      shouldFocusPriceRef.current = false;
    } else {
      // Enter: фокус переходить на інпут ціни
      shouldFocusPriceRef.current = true;
    }
    
    // Скинути збережене значення
    lastShiftKeyRef.current = false;
    
    onAddItem(event);
    addItemFormRef.current?.reset();
  };

  const handlePriceEnter = () => {
    // Коли натискають Enter в інпуті ціни, фокусуємо форму додавання товару
    addItemFormRef.current?.focus();
  };

  const handleAddItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // Перевірити чи подія вже оброблена (наприклад, коли вибрано хінт)
      if (e.defaultPrevented) {
        return;
      }
      
      // Зберегти shiftKey для використання в handleAddItem
      lastShiftKeyRef.current = e.shiftKey || false;
      
      e.preventDefault();
      if (e.shiftKey) {
        // Shift + Enter: фокус залишається на інпуті додавання товару
        shouldFocusPriceRef.current = false;
      } else {
        // Enter: фокус переходить на інпут ціни
        shouldFocusPriceRef.current = true;
      }
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  return (
    <div className="category-section">
      <CategoryInput
        ref={categoryInputRef}
        value={category.name}
        categories={categories}
        onChange={onUpdateName}
        onRemove={onRemove}
      />
      
      <div className="item-list">
        {category.items
          .sort((a, b) => {
            if (a.checked !== b.checked) {
              return a.checked ? 1 : -1;
            }
            const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
            const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
            return dateA - dateB;
          })
          .map((item: ShoppingItem) => {
            // Створити ref для кожного товару якщо його ще немає
            if (!itemRefs.current[item.id]) {
              itemRefs.current[item.id] = React.createRef<CategoryItemRef>();
            }
            
            return (
              <CategoryItem
                key={item.id}
                ref={itemRefs.current[item.id]}
                item={item}
                priceMode={priceMode}
                onToggle={() => onToggleItem(item.id)}
                onUpdatePrice={(price) => onUpdatePrice(item.id, price)}
                onUpdateName={onUpdateItemName ? (name) => onUpdateItemName(item.id, name) : undefined}
                onSavePrice={onSavePrice ? () => onSavePrice(item.id) : undefined}
                onRemove={() => onRemoveItem(item.id)}
                onPriceEnter={handlePriceEnter}
                categoryId={category.apiCategoryId}
              />
            );
          })}
      </div>
      
      <AddItemForm 
        ref={addItemFormRef} 
        onSubmit={handleAddItem}
        onKeyDown={handleAddItemKeyDown}
        categoryId={category.apiCategoryId}
      />
      
      <div className="category-total">
        Всього: <span>{formatCurrency(total)}</span>
      </div>
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';

export default CategoryCard;
